import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { cn } from "@love-cpmd/ui";
import { Bell } from "lucide-react";
import { Link } from "react-router-dom";
import * as api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useAction, useNotifications } from "../../lib/queries";
import { NotificationList } from "./notifications";

export function useUnreadCount() {
  const { userId } = useAuth();
  const { data } = useNotifications(userId);
  return data?.filter((n) => !n.readAt).length ?? 0;
}

function BellIcon({ count }: { count: number }) {
  return (
    <>
      <Bell className="size-5" aria-hidden />
      {count > 0 && (
        <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[10px] leading-none font-bold text-on-error ring-2 ring-surface">
          {count > 9 ? "9+" : count}
        </span>
      )}
    </>
  );
}

const bellButton =
  "relative inline-flex size-10 items-center justify-center rounded-control text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface data-open:bg-surface-container data-open:text-on-surface";

/** Desktop: dropdown. */
export function NotificationBell() {
  const { userId } = useAuth();
  const { data = [] } = useNotifications(userId);
  const unread = data.filter((n) => !n.readAt).length;
  const markAll = useAction(api.markAllNotificationsRead);
  return (
    <Popover className="relative">
      <PopoverButton className={bellButton} aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}>
        <BellIcon count={unread} />
      </PopoverButton>
      <PopoverPanel
        transition
        anchor="bottom end"
        className="z-[70] flex w-[380px] origin-top-right flex-col rounded-2xl border border-outline-variant/50 bg-surface-container-lowest shadow-pop transition duration-150 ease-out [--anchor-gap:8px] data-closed:scale-95 data-closed:opacity-0"
      >
        {({ close }) => (
          <>
            <div className="flex items-center justify-between border-b border-outline-variant/40 px-4 py-3">
              <p className="text-headline-sm text-on-surface">Notifications</p>
              {unread > 0 && userId && (
                <button type="button" onClick={() => markAll.fire(userId)} className="text-label-md text-primary hover:underline">
                  Mark all as read
                </button>
              )}
            </div>
            <div className="max-h-[420px] overflow-y-auto p-1.5">
              <NotificationList items={data.slice(0, 8)} compact onItemClick={() => close()} />
            </div>
            <Link
              to="/notifications"
              onClick={() => close()}
              className="border-t border-outline-variant/40 px-4 py-3 text-center text-label-md text-primary hover:bg-surface-container-low"
            >
              View all notifications
            </Link>
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
}

/** Mobile: the bell opens the full notifications page. */
export function NotificationBellLink({ className }: { className?: string }) {
  const count = useUnreadCount();
  return (
    <Link to="/notifications" className={cn(bellButton, className)} aria-label={`Notifications${count ? `, ${count} unread` : ""}`}>
      <BellIcon count={count} />
    </Link>
  );
}
